import { PaginationType } from '@nxsys/types/models/Common';
import { P6p9RecordType } from '@nxsys/types/models/hmrcUpdates/p6p9';
import { createSlice } from '@reduxjs/toolkit';
import { hmrcUpdatesAct } from '../action';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';
import { StudentLoanRecordType } from '@nxsys/types/models/hmrcUpdates/studentLoan';
import { GetHMRCP6p9UpdatesParams, GetStudentLoanUpdatesParams } from '../types';

export interface HmrcUpdatesState {
  loading: boolean;
  p6p9: {
    pending: {
      list: P6p9RecordType[];
      pagination: PaginationType;
      queryParams: GetHMRCP6p9UpdatesParams | null;
    };
    appliedCancelled: {
      list: P6p9RecordType[];
      pagination: PaginationType;
      queryParams: GetHMRCP6p9UpdatesParams | null;
    };
  };
  studentLoan: {
    pending: {
      list: StudentLoanRecordType[];
      pagination: PaginationType;
      queryParams: GetStudentLoanUpdatesParams | null;
    };
    appliedCancelled: {
      list: StudentLoanRecordType[];
      pagination: PaginationType;
      queryParams: GetStudentLoanUpdatesParams | null;
    };
  };
}

const initialState: HmrcUpdatesState = {
  loading: false,
  p6p9: {
    pending: {
      list: [],
      pagination: {
        current: 1,
        pageSize: 20,
        totalDocs: 0,
      },
      queryParams: null,
    },
    appliedCancelled: {
      list: [],
      pagination: {
        current: 1,
        pageSize: 20,
        totalDocs: 0,
      },
      queryParams: null,
    },
  },
  studentLoan: {
    pending: {
      list: [],
      pagination: {
        current: 1,
        pageSize: 20,
        totalDocs: 0,
      },
      queryParams: null,
    },
    appliedCancelled: {
      list: [],
      pagination: {
        current: 1,
        pageSize: 20,
        totalDocs: 0,
      },
      queryParams: null,
    },
  },
};

export const hmrcUpdatesSlice = createSlice({
  name: 'hmrcUpdates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(hmrcUpdatesAct.getHMRCP6p9Pending.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      hmrcUpdatesAct.getHMRCP6p9Pending.fulfilled,
      (state, action) => {
        state.loading = false;
        state.p6p9.pending.list = action.payload.data;
        state.p6p9.pending.pagination = action.payload.pagination;
        state.p6p9.pending.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getHMRCP6p9Pending.rejected,
      (state, action) => {
        state.loading = false;
        state.p6p9.pending.list = [];
        state.p6p9.pending.pagination = {
          current: 1,
          pageSize: 20,
          totalDocs: 0,
        };
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: 'hmrcUpdates.p6p9.getError' }),
        });
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getHMRCP6p9AppliedCancelled.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getHMRCP6p9AppliedCancelled.fulfilled,
      (state, action) => {
        state.loading = false;
        state.p6p9.appliedCancelled.list = action.payload.data;
        state.p6p9.appliedCancelled.pagination = action.payload.pagination;
        state.p6p9.appliedCancelled.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getHMRCP6p9AppliedCancelled.rejected,
      (state, action) => {
        state.loading = false;
        state.p6p9.appliedCancelled.list = [];
        state.p6p9.appliedCancelled.pagination = {
          current: 1,
          pageSize: 20,
          totalDocs: 0,
        };
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: 'hmrcUpdates.p6p9.getError' }),
        });
      },
    );
    builder.addCase(hmrcUpdatesAct.applyHMRCP6p9.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(hmrcUpdatesAct.applyHMRCP6p9.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.success) {
        showToast({
          type: 'success',
          message: appIntl().formatMessage({
            id: 'hmrcUpdates.p6p9.applySuccess',
          }),
        });
        return;
      }
      action.payload.errors.forEach((error: any) => {
        showToast({
          type: error?.isWarning ? 'warning' : 'error',
          message: error.error,
        });
      });
    });
    builder.addCase(hmrcUpdatesAct.applyHMRCP6p9.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'hmrcUpdates.p6p9.applyError' }),
      });
    });
    // Single apply P6P9
    builder.addCase(hmrcUpdatesAct.applySingleHMRCP6p9.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(hmrcUpdatesAct.applySingleHMRCP6p9.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.success) {
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: 'hmrcUpdates.p6p9.applySuccess' }),
        });
        return;
      }
      action.payload.errors?.forEach((error: any) => {
        showToast({
          type: error?.isWarning ? 'warning' : 'error',
          message: error.error,
        });
      });
    });
    builder.addCase(hmrcUpdatesAct.applySingleHMRCP6p9.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'hmrcUpdates.p6p9.applyError' }),
      });
    });
    builder.addCase(hmrcUpdatesAct.cancelHMRCP6p9.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(hmrcUpdatesAct.cancelHMRCP6p9.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'hmrcUpdates.p6p9.cancelSuccess',
        }),
      });
    });
    builder.addCase(hmrcUpdatesAct.cancelHMRCP6p9.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'hmrcUpdates.p6p9.cancelError' }),
      });
    });
    builder.addCase(hmrcUpdatesAct.hmrcSync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(hmrcUpdatesAct.hmrcSync.fulfilled, (state, action) => {
      state.loading = false;
      // The endpoint answers 200 even when HMRC refused a company's
      // credentials, so a green toast here hid a dead feed for 97 days
      // (NX-5832).
      const payload = action.payload as { success?: boolean; message?: string };
      if (payload?.success === false) {
        showToast({
          type: 'error',
          message:
            payload?.message ||
            appIntl().formatMessage({ id: 'hmrc.sync.error' }),
        });
        return;
      }
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'hmrc.sync.success' }),
      });
    });
    builder.addCase(hmrcUpdatesAct.hmrcSync.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'hmrc.sync.error' }),
      });
    });
    // Student Loan
    builder.addCase(hmrcUpdatesAct.getStudentLoanPending.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      hmrcUpdatesAct.getStudentLoanPending.fulfilled,
      (state, action) => {
        state.loading = false;
        state.studentLoan.pending.list = action.payload.data;
        state.studentLoan.pending.pagination = action.payload.pagination;
        state.studentLoan.pending.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getStudentLoanPending.rejected,
      (state, action) => {
        state.loading = false;
        state.studentLoan.pending.list = [];
        state.studentLoan.pending.pagination = {
          current: 1,
          pageSize: 20,
          totalDocs: 0,
        };
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: 'hmrcUpdates.studentLoan.getError' }),
        });
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getStudentLoanAppliedCancelled.pending,
      (state) => {
        state.loading = true;
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getStudentLoanAppliedCancelled.fulfilled,
      (state, action) => {
        state.loading = false;
        state.studentLoan.appliedCancelled.list = action.payload.data;
        state.studentLoan.appliedCancelled.pagination =
          action.payload.pagination;
        state.studentLoan.appliedCancelled.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      hmrcUpdatesAct.getStudentLoanAppliedCancelled.rejected,
      (state, action) => {
        state.loading = false;
        state.studentLoan.appliedCancelled.list = [];
        state.studentLoan.appliedCancelled.pagination = {
          current: 1,
          pageSize: 20,
          totalDocs: 0,
        };
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: 'hmrcUpdates.studentLoan.getError' }),
        });
      },
    );
    builder.addCase(hmrcUpdatesAct.applyStudentLoan.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      hmrcUpdatesAct.applyStudentLoan.fulfilled,
      (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          showToast({
            type: 'success',
            message: appIntl().formatMessage({
              id: 'hmrcUpdates.studentLoan.applySuccess',
            }),
          });
          return;
        }
        action.payload.errors.forEach((error: any) => {
          showToast({
            type: error?.isWarning ? 'warning' : 'error',
            message: error.error,
          });
        });
      },
    );
    builder.addCase(
      hmrcUpdatesAct.applyStudentLoan.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'hmrcUpdates.studentLoan.applyError',
            }),
        });
      },
    );
    // Single apply Student Loan
    builder.addCase(hmrcUpdatesAct.applySingleStudentLoan.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(hmrcUpdatesAct.applySingleStudentLoan.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.success) {
        showToast({
          type: 'success',
          message: appIntl().formatMessage({ id: 'hmrcUpdates.studentLoan.applySuccess' }),
        });
        return;
      }
      action.payload.errors?.forEach((error: any) => {
        showToast({
          type: error?.isWarning ? 'warning' : 'error',
          message: error.error,
        });
      });
    });
    builder.addCase(hmrcUpdatesAct.applySingleStudentLoan.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'hmrcUpdates.studentLoan.applyError' }),
      });
    });
    builder.addCase(hmrcUpdatesAct.cancelStudentLoan.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(hmrcUpdatesAct.cancelStudentLoan.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          id: 'hmrcUpdates.studentLoan.cancelSuccess',
        }),
      });
    });
    builder.addCase(
      hmrcUpdatesAct.cancelStudentLoan.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'hmrcUpdates.studentLoan.cancelError',
            }),
        });
      },
    );
  },
});

export const hmrcUpdatesReducer = hmrcUpdatesSlice.reducer;
export const hmrcUpdatesActions = {
  ...hmrcUpdatesSlice.actions,
  ...hmrcUpdatesAct,
};
